import {RagState} from './data/reportRowModel';

/**
 * The project-status vocabulary, in one place.
 *
 * Three surfaces read a project's RAG status - the table cell, the group heading, and the filter
 * value picker - and a reader who meets two of them must not meet two different vocabularies. That
 * is an acceptance criterion, not a tidiness preference, so the mapping lives here rather than being
 * spelled out again at each surface.
 */

/** The four values `projectStatus.rag` is published with, as `project_status.json` spells them and
 * in the order it lists them. A filter condition carries the WIRE value, so this is the id side of
 * every control; the label is a display decision made below. */
const RAG_WIRE_VALUES = ['Green', 'Yellow', 'Red', 'No or other status'] as const;

/** The em dash a project with no usable status shows. Exported so a caller can assert on the
 * intended character rather than on a hyphen that happens to look like one.
 *
 * Deliberately NOT a translatable message: it is punctuation standing in for an absent value, the
 * same in every locale, and handing a translator a lone dash invites it being replaced with a word -
 * which is the "no or other status" wording that was rejected in the first place. */
export const PROJECT_STATUS_EM_DASH = '—';

/** Wire value -> state. Only the three healthy values map to a named state; `No or other status`
 * and anything unrecognised both land on `unknown`, which is what stops a project nobody has set a
 * status on from being presented as healthy.
 *
 * A `Map` rather than an object literal because the key is a raw wire string: an object lookup
 * answers for `constructor`, `toString` and `__proto__` off the prototype chain, so a value the
 * wire happened to spell that way would resolve to a function instead of falling through to
 * `unknown`. */
const STATE_BY_WIRE_VALUE = new Map<string, RagState>([
	['Green', 'on_track'],
	['Yellow', 'at_risk'],
	['Red', 'off_track'],
	['No or other status', 'unknown'],
]);

/** A translatable string, in the shape react-intl's `formatMessage` takes. Declared structurally
 * rather than imported from react-intl so this module stays a plain unit with no React dependency:
 * the row mapper that calls it is a pure function and cannot be a hook. */
export interface LabelDescriptor {
	id: string;
	defaultMessage: string;
}

/** What resolves a descriptor to display text. `IntlShape['formatMessage']` satisfies it, so a
 * caller inside React passes `intl.formatMessage` straight in. */
export type FormatLabel = (descriptor: LabelDescriptor) => string;

/** Used when no formatter is supplied. Every caller that can reach React should pass one; this
 * keeps a label honest (and English) on the pure paths - tests, fixtures, and any mapping that runs
 * before an intl context exists - rather than rendering a bare message id. */
export const englishLabel: FormatLabel = descriptor => descriptor.defaultMessage;

/** DECIDED with product: the design's wording, not the colour names the wire uses. The fourth state
 * maps to an explicit `null`, not to a missing key: its label is the em dash constant above, which
 * is not translated. A TOTAL record rather than a `Partial` - and for the same reason `RAG_ICON` in
 * `CellRenderer` is total - so a state added later fails to compile until someone decides here
 * whether it has a message; a `Partial` would let it default silently to the em dash, which is a
 * judgement in itself. */
const RAG_MESSAGE: Record<RagState, LabelDescriptor | null> = {
	on_track: {id: 'ai_report_builder.project_status.on_track', defaultMessage: 'On Track'},
	at_risk: {id: 'ai_report_builder.project_status.at_risk', defaultMessage: 'At Risk'},
	off_track: {id: 'ai_report_builder.project_status.off_track', defaultMessage: 'Off Track'},
	unknown: null,
};

/** The record above, as the `Map` the lookup actually goes through. The record is what gives the
 * compiler its exhaustiveness check; the `Map` is what keeps the lookup honest at runtime, since
 * `ragStateLabel` is exported and an object read for a state spelled `toString` or `constructor`
 * would answer with a function rather than falling through to the em dash. */
const RAG_MESSAGE_BY_STATE = new Map<string, LabelDescriptor>(
	Object.entries(RAG_MESSAGE).filter((entry): entry is [string, LabelDescriptor] => entry[1] !== null)
);

/**
 * The state a raw `projectStatus.rag` value means.
 *
 * Takes `unknown` rather than a string: the value arrives from the wire through the row mapper's
 * `FieldNodeValue`, which is legitimately a number, null, or absent, and a report-service a version
 * ahead can publish a fifth value. Every one of those is a project we cannot describe, so they all
 * answer `unknown` instead of throwing or falling through to a healthy state.
 */
export function ragStateFor(wireValue: unknown): RagState {
	if (typeof wireValue !== 'string') {
		return 'unknown';
	}
	return STATE_BY_WIRE_VALUE.get(wireValue) ?? 'unknown';
}

/** What a reader is shown for a state already resolved. Never empty: the statusless state shows the
 * em dash, which says we do not know, where a blank cell says nothing at all. Callers that already
 * hold the state use this rather than re-deriving it from the wire value. */
export function ragStateLabel(state: RagState, formatLabel: FormatLabel = englishLabel): string {
	const message = RAG_MESSAGE_BY_STATE.get(state);
	return message ? formatLabel(message) : PROJECT_STATUS_EM_DASH;
}

/** What a reader is shown for a raw `projectStatus.rag` value. Never empty: an unusable value
 * shows the em dash, which says we do not know, where a blank cell says nothing at all. */
export function ragLabelFor(wireValue: unknown, formatLabel: FormatLabel = englishLabel): string {
	return ragStateLabel(ragStateFor(wireValue), formatLabel);
}

/** The four states as a picker's candidates: the wire value as the id a condition stores, the
 * display label beside it. */
export function projectStatusCandidates(formatLabel: FormatLabel = englishLabel): {id: string; label: string}[] {
	return RAG_WIRE_VALUES.map(value => ({id: value, label: ragLabelFor(value, formatLabel)}));
}
